import React, { useMemo } from "react";
import "./styles.scss";
import {
  faGavel,
  faRightFromBracket,
  faUser
} from "@fortawesome/free-solid-svg-icons";
import { useHistory, useLocation } from "react-router-dom";
import { links } from "./utils";
import {
  ACCOUNT,
  LEGAL,
  SIGNUP,
  SUBSCRIPTION
} from "../../../utils/routes/routes";
import { buildPathWithTrackingId } from "../../../utils/routing/routingUtils";
import { tt } from "../../../utils/i18n/i18n";
import { deleteMasterTokenFromStorage } from "../../../utils/helpers/localStorage";
import {
  setAccountToDisplay,
  setManagedAccounts,
  setMasterToken,
  useAccountToDisplaySelector
} from "../../../redux/actions/user";
import { type LinksSidebarI } from "./types";
import { useDispatch, useSelector } from "react-redux";
import KC_Navlink from "../../../designsystem/KC_Navlink/KC_Navlink";
import TestsID from "../../../utils/TestsID";
import KC_Button, {
  KC_ButtonSize
} from "../../../designsystem/KC_Button/KC_Button";
import NiceModal from "@ebay/nice-modal-react";
import PlanOptionModal from "../../../components/modal/PlanOption/PlanOptionModal";
import { setJetpackConfig } from "../../../redux/jetpackConfig";
import KC_Toggle from "../../../designsystem/KC_FormInput/KC_Toggle";
import { setDebug } from "../../../redux/actions/app";
import { RootStateI } from "../../../redux";
import CONFIG from "../../../model/TSConfig";

export const LinksSidebar = (props: LinksSidebarI) => {
  const location = useLocation();
  const history = useHistory();
  const dispatch = useDispatch();
  const { accountToDisplay } = useAccountToDisplaySelector();

  const isDebug = useSelector((s: RootStateI) => s.app.isDebug);

  const isHasBestTierSub = useMemo(() => {
    return !!accountToDisplay?.saasFeatures?.ip;
  }, [accountToDisplay]);

  const isCurrentRoute = (to: string) => {
    return location.pathname.includes(to.replace("?", ""));
  };

  const onCloseSidebarMobile = () => {
    props.closeSidebar?.();
  };

  function onLogout() {
    onCloseSidebarMobile();
    deleteMasterTokenFromStorage();
    dispatch(setJetpackConfig(undefined));
    dispatch(setMasterToken(undefined));
    dispatch(setManagedAccounts([]));
    dispatch(setAccountToDisplay(undefined));
    history.push(SIGNUP + "?login=1");
  }

  const displayUpsellButton = useMemo(() => {
    if (location.pathname.includes(SUBSCRIPTION)) {
      return false;
    }
    if (accountToDisplay?.subscriptionPromoCode) {
      return false;
    }
    return !accountToDisplay?.saasFeatures?.ip;
  }, [accountToDisplay, location]);

  const linksWithTracking = useMemo(() => {
    return links.map((link) => ({
      ...link,
      path: buildPathWithTrackingId(link.path, accountToDisplay?.trackingID)
    }));
  }, [links, accountToDisplay]);

  return (
    <div className="link-wrapper">
      <div className="ls-links px-4">
        {linksWithTracking.map((link, i) => {
          const isActive = isCurrentRoute(link.path);

          /**
           * Disable the link to POSAs if
           * POSA shipping is not possible in current country.
           */
          if (
            link.text === tt("posa_short") &&
            accountToDisplay?.businessUser?.shippingAddress?.countryCode?.toUpperCase() !==
              "FR"
          ) {
            return null;
          }

          return (
            <KC_Navlink
              id={`sidebar_navlink_${link.text}`}
              key={link.path + i}
              to={link.path}
              isActive={isActive}
              text={link.text}
              icon={link.icon}
              onClick={onCloseSidebarMobile}
            />
          );
        })}
      </div>
      <div className="flex-grow-1">&nbsp;</div>
      <div className="mt-auto">
        <div className="mt-4 px-4 cursor-pointer">
          {!CONFIG.publicURL?.includes("kadow.club") && (
            <div
              className="d-flex flex-row mt-2 mb-2"
              style={{ paddingLeft: 8 }}
            >
              <KC_Toggle
                id="debug"
                isChecked={isDebug}
                onToggle={() => {
                  dispatch(setDebug(!isDebug));
                }}
              />
              <span className="d-block ml-2 ">{tt("debug_text_debug")}</span>
            </div>
          )}
          <KC_Navlink
            id="my-account"
            key={ACCOUNT}
            to={buildPathWithTrackingId(ACCOUNT, accountToDisplay?.trackingID)}
            isActive={isCurrentRoute(ACCOUNT)}
            text={tt("my_account")}
            icon={faUser}
            onClick={onCloseSidebarMobile}
          />
          <KC_Navlink
            id="legal"
            key={LEGAL}
            to={LEGAL}
            isActive={isCurrentRoute(LEGAL)}
            text={tt("legal")}
            icon={faGavel}
            onClick={onCloseSidebarMobile}
          />
          <KC_Navlink
            id={TestsID.LOGOUT_BUTTON}
            key={SIGNUP}
            to={SIGNUP}
            isActive={false}
            text={tt("logout")}
            icon={faRightFromBracket}
            onClick={onLogout}
          />
        </div>

        {displayUpsellButton && (
          <div className="p-3 border-top mt-3 d-flex flex-column st-gap">
            {!isHasBestTierSub && (
              <KC_Button
                id="upsell-btn"
                isUpsell={true}
                size={KC_ButtonSize.Large}
                text={tt("upsell_premium_button")}
                onClick={() => {
                  onCloseSidebarMobile();
                  void NiceModal.show(PlanOptionModal);
                }}
                className="d-flex"
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
};
